<template>
  <div>
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :title="confirmationTitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
      ref="confirmationModal"
    />
    
    <!-- Expense Modal-->
    <v-dialog
      width="480px"
      :overlay-opacity="0.90"
      v-model="dialog"
    >
      <v-card>
        <v-card-title class="d-block">
          <div class="font-weight-bold">{{item.id ? "Update" : "Add"}} Expense</div>
          <p class="caption mb-4">Enter expense type, amount and description details below.</p>
        </v-card-title>
        <v-card-text class="py-2">

          <v-form
            @submit.prevent="addItem()"
          >
            <ValidationObserver ref="validationObserver">

              <ValidationProvider
                name="Expense Type"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  dense
                  :autofocus="true"
                  label="Expense Type"
                  placeholder="Select expense type"
                  :items="expenseTypes"
                  item-text="accountName"
                  item-value="accountName"
                  v-model="item.accountName"
                  :error-messages="errors"
                >
                  <template v-slot:label>
                    Expense Type
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>

              <ValidationProvider
                name="Amount"
                :rules="'required|min_value:0|max_value:999999999999'"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  dense
                  required
                  label="Amount"
                  v-model="item.amount"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                />
              </ValidationProvider>
              
              <ValidationProvider
                name="Description"
                :rules="'max:1500'"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  dense
                  label="Expense Description"
                  v-model="item.description"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </ValidationObserver>

            <v-card-actions class="pt-5 justify-end">
              <v-btn
                @click="closeModal()"
                min-width="120"
                min-height="42"
                depressed
                class="text-capitalize"
              >Cancel</v-btn>
              <v-btn
                type="submit"
                min-width="120"
                min-height="42"
                depressed
                color="primary"
                class="text-capitalize"
              >Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <v-row align="center" justify="space-between">
      <v-col cols="12" md="5"><h3>Expenses</h3></v-col>
      <v-col cols="12" md="6" class="text-md-right">
        <v-btn
          @click="openModal"
          class="text-capitalize"
          depressed
          color="primary"
          >Add Expense</v-btn
        >
      </v-col>
    </v-row>
    <v-card class="mt-5" outlined rounded="lg">
      <v-data-table :items="expenses" hide-default-footer :headers="headers">
        <!-- Customized No Data slot -->
        <template #no-data>
          <v-row justify="center">
            <v-col>
              <NoData />
              <p class="text-center mt-5">No Expense Items</p>
            </v-col>
          </v-row>
        </template>
        
        <template #[`item.accountName`]="{ item }">
          <div class="my-4">
            <span class="text-capitalize">{{item.accountName}}</span>
          </div>
        </template>
        
        <template #[`item.amount`]="{ item }">
          {{asMoney(item.amount)}}
        </template>
        
        <template #[`item.description`]="{ item }">
          <span class="text-wrap text-capitalize">{{item.description || "No description"}}</span>
        </template>
        
        <!-- Action slot -->
        <template #[`item.actions`]="{ item }">
          <v-btn
            @click="openModal(item)"
            class="text-capitalize"
            text
            color="primary"
          >
            Edit</v-btn>
          <v-btn
            @click="removeItem(item)"
            class="text-capitalize"
            text
            color="error"
          >
            Remove</v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';

export default {
  mixins: [PageMixin],
  data() {
    return {
      /** @type {import('vuetify').DataTableHeader[]}*/
      headers: [
        { text: 'Expense', value: 'accountName', sortable: false },
        { text: 'Amount', value: 'amount', sortable: false },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      dialog: false,
      item: {},

      VuetifyMoneyOptions: VuetifyMoneyOptions,
      confirmationTitle: "",
      confirmationMessage: "",
    };
  },

  props: {
    expenses: {
      type: Array,
      default: () =>([])
    },
    expenseTypes: {
      type: Array,
      default: () =>([])
    } 
  },

  methods: {
    openModal(item) {
      this.$refs.validationObserver?.reset();
      this.item = cloneDeep(item) || {};
      this.dialog = true;
    },

    closeModal(item) {
      this.dialog = false;
      this.item = item || {};
      this.$refs.validationObserver?.reset();
    },

    async addItem() {
      const expenses = cloneDeep(this.expenses)
      const valid = await this.$refs.validationObserver.validate();
      if(!valid) return;
      
      if(this.item.id) {
        const index = expenses.findIndex((el) => el.id == this.item.id)
        expenses.splice(index, 1, {...this.item});
      } else {
        this.item.id = v4(); 
        expenses.push(this.item);
      }

      this.$emit('update', expenses) ;
      this.closeModal();
    },
  
    removeItem(item) {
      this.confirmationTitle = `Confirm Deletion`;
      this.confirmationMessage = `Are you sure you want to delete expense '${item.accountName}'?`;
      this.$refs.confirmationModal.show(async () => {
        const expenses = this.expenses.filter((el) => el.id !== item.id)
        this.$emit('update', expenses) 
      });
    }
  }
};
</script>
>
